import { uniqBy, isEmpty } from 'lodash';

import { combineSearchAndStateCourses } from 'modules/CoursePlanPreview/selectors';

// eslint-disable-next-line complexity
export default (
  state = {
    loading: false,
    loadingCourse: false,
    onboardingStatus: undefined,
    requirementDetails: undefined,
    courseSearchStatus: {
      count: undefined,
      next: undefined,
      previous: undefined,
      subjects: [],
      tags: [],
      grades: [],
    },
    rawRequirementCourses: [],
    backgroundLoading: false,
    courseBankSearchText: '',
    loadingTopLevelRequirementId: undefined,
    activeGrade: undefined,
  },
  action,
) => {
  switch (action.type) {
    case 'POPULATE_COURSE_PLAN_PREVIEW_ONBOARDING_STATUS':
      return {
        ...state,
        onboardingStatus: action.mode,
      };

    case 'POPULATE_COURSE_PLAN_PREVIEW_REQUIREMENT_DETAILS':
      return {
        ...state,
        requirementDetails: action?.requirementDetails,
        rawRequirementCourses:
          action?.requirementDetails?.pickerConfig?.pickerDetails?.courses ??
          [],
      };

    case 'SET_COURSE_PLAN_PREVIEW_LOADING':
      return {
        ...state,
        loading: action.mode,
      };

    case 'SET_COURSE_PLAN_PREVIEW_LOADING_COURSE_STATUS':
      return {
        ...state,
        loadingCourse: action.status,
      };

    case 'COURSE_PLAN_PREVIEW.SET_COURSE_PLAN_PREVIEW_LOADING':
      return {
        ...state,
        backgroundLoading: action.mode,
      };

    case 'COURSE_PLAN_PREVIEW.REMOVE_NEXT_SEARCH_LINK':
      return {
        ...state,
        courseSearchStatus: {
          ...state.courseSearchStatus,
          next: undefined,
        },
      };

    case 'COURSE_PLAN_PREVIEW.SET_ACTIVE_GRADE':
      return {
        ...state,
        activeGrade: action.grade,
      };

    case 'APPEND_COURSE_PLAN_PREVIEW_COURSES':
      // eslint-disable-next-line
      const hasSequences =
        state?.requirementDetails?.pickerConfig?.pickerDetails?.sequences
          ?.length > 0;
      // eslint-disable-next-line
      const sequences =
        state?.requirementDetails?.pickerConfig?.pickerDetails?.sequences?.map(
          entry => ({
            ...entry,
            courses: entry.sequenceSelected
              ? uniqBy(
                  [
                    ...(entry.courses || []).map(existingCourse => {
                      const existingCourseInSearchResult =
                        action.results.filter(
                          c => c.uuid === existingCourse.uuid,
                        )?.[0] ?? {};
                      return {
                        ...existingCourse,
                        ...existingCourseInSearchResult,
                        termsSelectable: uniqBy(
                          (existingCourse?.termsSelectable ?? []).concat(
                            existingCourseInSearchResult?.termsSelectable ?? [],
                          ),
                          v => `${v.grade}-${v.academicTerm.academicTerm}`,
                        ),
                        termsSelected: uniqBy(
                          (existingCourse?.termsSelected ?? []).concat(
                            existingCourseInSearchResult?.termsSelected ?? [],
                          ),
                          v => `${v.grade}-${v.academicTerm.academicTerm}`,
                        ),
                      };
                    }),
                    ...action.results,
                  ],
                  'uuid',
                )
              : entry.courses,
          }),
        );
      return {
        ...state,
        courseSearchStatus: {
          count: action.count,
          next: action.next,
          previous: action.previous,
          subjects: action.subjects,
          tags: action.tags,
          grades: action.grades,
        },
        requirementDetails: state.requirementDetails
          ? {
              ...state.requirementDetails,
              pickerConfig: hasSequences
                ? {
                    ...state.requirementDetails.pickerConfig,
                    pickerDetails: {
                      ...state.requirementDetails.pickerConfig.pickerDetails,
                      sequences,
                    },
                  }
                : {
                    ...state.requirementDetails.pickerConfig,
                    pickerDetails: {
                      ...(state.requirementDetails.pickerConfig
                        ?.pickerDetails || {}),
                      ...(Array.isArray(
                        state.requirementDetails.pickerConfig?.pickerDetails
                          ?.courses,
                      )
                        ? {
                            courses: combineSearchAndStateCourses({
                              stateCourses:
                                state.requirementDetails.pickerConfig
                                  .pickerDetails.courses,
                              searchCourses: action.results,
                            }),
                          }
                        : {}),
                    },
                  },
            }
          : undefined,
      };
    case 'RESET_COURSE_PLAN_PREVIEW_COURSES':
      return {
        ...state,
        requirementDetails: state.requirementDetails
          ? {
              ...state.requirementDetails,
              pickerConfig: {
                ...state.requirementDetails.pickerConfig,
                pickerDetails: {
                  ...state.requirementDetails?.pickerConfig?.pickerDetails,
                  ...(Array.isArray(
                    state.requirementDetails?.pickerConfig?.pickerDetails
                      ?.courses,
                  )
                    ? {
                        courses: uniqBy(state.rawRequirementCourses, 'uuid'),
                      }
                    : {}),
                },
              },
            }
          : undefined,
      };

    case 'COURSE_PLAN_PREVIEW.RESET_COURSE_PLAN_PREVIEW_COURSES':
      return {
        ...state,
        courseSearchStatus: {
          count: undefined,
          next: undefined,
          previous: undefined,
          subjects: [],
          tags: [],
          grades: [],
        },
      };

    case 'COURSE_PLAN_PREVIEW.PERSIST_COURSE_BANK_SEARCH_TEXT':
      return {
        ...state,
        courseBankSearchText: action.courseBankSearchText,
      };

    case 'COURSE_PLAN_PREVIEW.SET_LOADING_TOP_LEVEL_REQUIREMENT':
      return {
        ...state,
        loadingTopLevelRequirementId: action.topLevelRequirementId,
      };
    case 'COURSE_PLAN_PREVIEW.ADD_COURSE_TO_REDUX_REQUIREMENT': {
      const courses =
        state?.requirementDetails?.pickerConfig?.pickerDetails?.courses ?? [];
      const payloadCourses = Array.isArray(action.coursePayload)
        ? action.coursePayload
        : [action.coursePayload];
      const existingCourses = payloadCourses.filter(c =>
        courses.find(e => e.uuid === c.rawCourse.uuid),
      );
      const newCourses = payloadCourses.filter(
        c => !courses.find(e => e.uuid === c.rawCourse.uuid),
      );
      return {
        ...state,
        requirementDetails: {
          ...state.requirementDetails,
          pickerConfig: {
            ...state.requirementDetails.pickerConfig,
            pickerDetails: {
              ...state.requirementDetails.pickerConfig.pickerDetails,
              courses: [
                ...courses.map(plannedCourse => {
                  const matchedCourses =
                    existingCourses.filter(
                      c => c.rawCourse.uuid === plannedCourse.uuid,
                    ) ?? {};
                  if (!isEmpty(matchedCourses)) {
                    return {
                      ...plannedCourse,
                      termsSelected: [
                        ...plannedCourse.termsSelected,
                        ...(matchedCourses || []).map(c => ({
                          grade: c.grade,
                          academicTerm: {
                            academicTerm: c.academicTerm,
                          },
                          additional: {
                            status: 'planned',
                            enrolled: false,
                            preselected: false,
                            creditCount: c.course.credits,
                            creditAwarded: null,
                          },
                        })),
                      ],
                    };
                  }
                  return plannedCourse;
                }),
                ...(newCourses || []).map(c => ({
                  ...(typeof c.course === 'object' ? c.course : {}),
                  ...(c.rawCourse || {}),
                  termsEligible: [],
                  termsSelectable: [],
                  termsSelected: [
                    {
                      grade: c.grade,
                      academicTerm: {
                        academicTerm: c.academicTerm,
                      },
                      additional: {
                        status: 'planned',
                        enrolled: false,
                        preselected: false,
                        creditCount: c.course.credits,
                        creditAwarded: null,
                      },
                    },
                  ],
                })),
              ],
            },
          },
        },
      };
    }

    default: {
      return state;
    }
  }
};
